import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import accountingClientsAPI from "../../api/accountingClientsAPI";
import { useAuth } from "../../context/AuthContext";
import Button from "../../components/Button";

import VATManagement from "../../components/VATManagement";
import Form2307Management from "../../components/Form2307Management";
import PercentageTaxFormManagement from "../../components/PercentageTaxFormManagement";
import Form2316Management from "../../components/Form2316Management";
import IncomeTaxManagement from "../../components/IncomeTaxManagement";
import ITRManagement from "../../components/ITRManagement";
import WithholdingTaxManagement from "../../components/wht/WithholdingTaxManagement";

import {
  DocumentTextIcon,
  ChartBarIcon,
  BriefcaseIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const TaxObligationsPage = () => {
  const { selectedClient, user } = useAuth();
  // Withholding tax (WHT) is an accounting-firm module; the API refuses every
  // other tenant type, so the entry is never offered to them.
  const isAccountingFirm = user?.tenantType === "accounting_firm";
  const [client, setClient] = useState(null);
  const [selectedObligation, setSelectedObligation] = useState(() => {
    const clientId = localStorage.getItem("accountingClientId") || "";
    if (!clientId) return null;
    return sessionStorage.getItem(`tax-obligation:selected:${clientId}`) || null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const activeClientId = selectedClient?._id || localStorage.getItem("accountingClientId") || "";

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        if (!activeClientId) {
          setClient(null);
          return;
        }

        const res = await accountingClientsAPI.getById(activeClientId);
        setClient(res.data.client);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Failed to load client");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [activeClientId]);

  const taxObligations = useMemo(() => {
    if (!client) return [];
    return [
      {
        key: "percentageTax",
        name: "Percentage Tax (BIR Form 2551Q)",
        description: "Quarterly percentage tax filing",
        applicable: !!client.taxObligations?.percentageTax,
        icon: ChartBarIcon
      },
      {
        key: "vat",
        name: "Value-Added Tax (BIR Form 2550Q)",
        description: "Quarterly VAT filing and records",
        applicable: !!client.taxObligations?.vat,
        icon: DocumentTextIcon
      },
      {
        key: "withholdingTaxExpanded",
        name: "Expended WithHolding Tax (BIR Form 2307)",
        description: "Expanded withholding tax – creditable against ITR",
        applicable: !!client.taxObligations?.withholdingTaxExpanded,
        icon: ChartBarIcon
      },
      {
        key: "withholdingTaxCompensation",
        name: "Withholding Tax on Compensation (BIR Form 2316)",
        description: "Employee compensation withholding",
        applicable: !!client.taxObligations?.withholdingTaxCompensation,
        icon: BriefcaseIcon
      },
      {
        key: "incomeTax",
        name: "Income Tax (BIR Form 1701 / 1702)",
        description: "Annual income tax filing and schedules",
        applicable: !!client.taxObligations?.incomeTax,
        icon: ArrowTrendingUpIcon
      },
      // Shown only when it applies (accounting firm + EWT or compensation
      // withholding on) — never as a greyed-out card.
      ...(isAccountingFirm && (client.taxObligations?.withholdingTaxExpanded || client.taxObligations?.withholdingTaxCompensation)
        ? [{
            key: "wht",
            name: "Withholding Tax (0619-E / 1601-EQ / 1601-C)",
            description: "EWT records, remittance returns and certificates",
            applicable: true,
            icon: DocumentTextIcon
          }]
        : [])
    ];
  }, [client, isAccountingFirm]);

  useEffect(() => {
    if (!activeClientId) return;
    const key = `tax-obligation:selected:${activeClientId}`;
    if (selectedObligation) {
      sessionStorage.setItem(key, selectedObligation);
    } else {
      sessionStorage.removeItem(key);
    }
  }, [activeClientId, selectedObligation]);

  const renderModule = () => {
    if (!selectedObligation || !client) return null;

    switch (selectedObligation) {
      case "vat":
        return <VATManagement client={client} />;
      case "withholdingTaxExpanded":
        return <Form2307Management client={client} />;
      case "percentageTax":
        return <PercentageTaxFormManagement client={client} />;
      case "withholdingTaxCompensation":
        return <Form2316Management client={client} />;
      case "incomeTax":
        return <ITRManagement client={client} />;
      case "wht":
        // A stale session selection must not open it for another tenant type.
        return isAccountingFirm ? <WithholdingTaxManagement client={client} /> : null;
      default:
        return (
          <div className="p-4 text-sm text-ink-body">
            Module not wired yet.
          </div>
        );
    }
  };

  return (
    <Layout pageTitle="Tax Obligations">
      {/*
        Widened from max-w-7xl (1280px). Every module rendered here is a wide
        line-item table — percentage tax alone has ten columns — and 1280px cut
        them off at standard desktop width. NOTE: this wrapper is shared by all
        five tax modules (VAT, 2307, percentage tax, 2316, ITR), so this affects
        their available width too; they all have wide tables and gain from it.
      */}
      <div className="max-w-[1536px] mx-auto p-6">
        {/* Guard: no selected company */}
        {!activeClientId && (
          <div className="bg-white border border-ink-line rounded-lg p-6">
            <h2 className="text-lg font-medium text-ink">No client selected</h2>
            <p className="text-sm text-ink-body mt-1">
              Choose a company from the top switcher.
            </p>
          </div>
        )}

        {activeClientId && (
          <>
            {loading && <div className="p-4 text-sm text-ink-body">Loading…</div>}
            {error && <div className="p-4 text-sm text-danger-500">Error: {error}</div>}

            {!loading && !error && client && (
              <>

                {/* Body */}
                <div className="bg-white rounded-lg shadow-sm border border-ink-line">
                  <div className="p-6">
                    {selectedObligation ? (
                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedObligation(null);
                            if (activeClientId) {
                              sessionStorage.removeItem(`tax-obligation:selected:${activeClientId}`);
                            }
                          }}
                          className="flex items-center text-primary-500 font-medium text-sm mb-4"
                        >
                          ← Back to obligations
                        </button>
                        {renderModule()}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {taxObligations.map((o) => {
                          const IconComponent = o.icon;
                          return (
                            <div
                              key={o.key}
                              className={`border rounded-lg p-4 transition-colors ${
                                o.applicable
                                  ? "border-ink-line bg-white hover:bg-primary-50/40 hover:border-primary-200 cursor-pointer"
                                  : "border-ink-line bg-ink-canvas opacity-60 cursor-not-allowed"
                              }`}
                              onClick={() => o.applicable && setSelectedObligation(o.key)}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <IconComponent className={`h-6 w-6 ${
                                  o.applicable ? "text-primary-500" : "text-ink-body"
                                }`} />
                                <span
                                  className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                                    o.applicable ? "bg-primary-50 text-primary-700" : "bg-ink-canvas text-ink-body"
                                  }`}
                                >
                                  {o.applicable ? "Applicable" : "Not applicable"}
                                </span>
                              </div>
                              <h3 className="font-medium text-ink mb-1 text-sm">{o.name}</h3>
                              <p className="text-sm text-ink-body mb-3">{o.description}</p>

                              {o.applicable ? (
                                <Button variant="outline" size="sm" fullWidth>
                                  Manage records
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" fullWidth disabled>
                                  Not applicable
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default TaxObligationsPage;
